import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FilledCompliance,
  TenantComplianceForm,
  TenantComplianceFormEntityType,
} from '../../../../../../backend-core-lib/app/common-shared/account/models';

import {
  decompressAndDecodeData,
  getShortDateFromDateStr,
} from '../../../../common-shared/utils';
import { ApiRequestBuilder, ApiService } from '../../../api';
import { cloneDeep, find, orderBy, sortBy } from 'lodash';
import {
  evaluateFormlyExpression,
  FormExportButton,
  FormExportConfig,
} from '../../../forms';
import { NotificationsService } from '../../../notifications';
import { ExpressionContainer } from '../../../../../../backend-core-lib/cross-lib';
import { getDateMonthStr } from '../../../../../../backend-core-lib/app/common-shared/utils/get-date-month-str';

interface ComplianceFormItem {
  status: string;
  severity: string;
  date: string;
  dateInt: number;
  canView: boolean;
  canApprove: boolean;
  isActive: boolean;
  filledCompliance: FilledCompliance;
}

@Component({
  selector: 'compliance-section',
  templateUrl: './compliance-section.component.html',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ComplianceSectionComponent implements OnChanges {
  @HostBinding('class')
  readonly cssClass = 'compliance-section';

  @Input()
  environmentUid!: string;

  @Input()
  entityType!: TenantComplianceFormEntityType;

  @Input()
  entityUid!: string;

  @Input()
  filledCompliance?: FilledCompliance[] = [];

  @Input()
  isAdmin = false;

  @Output()
  entityUpdated = new EventEmitter<any>();

  complianceFormItems: ComplianceFormItem[] = [];
  activeForm?: TenantComplianceForm;
  allForms: TenantComplianceForm[] = [];

  showSelectedForm = false;
  selectedFormModel: any = undefined;
  selectedFormConfig?: FormExportConfig;

  showFillNewForm = false;
  fillNewFormModel: any = undefined;
  fillNewFormConfig?: FormExportConfig;

  fillNewFormButtons: FormExportButton[] = [
    {
      actionId: 'submit',
      // con: 'pi pi-save',
      type: 'submit',
      label: 'common.info.forms.complianceForm.button.submit.label',
      translate: true,
      severity: 'primary',
    },
  ];

  /*showMessageCompliance = '';
  showMessageSeverity = '';
  showComplianceForm = false;
  requiresManualApproval = false;


  activeFilledForm?: FilledCompliance;
  previousActiveFilledForm?: FilledCompliance;

  formModel: any = {};
  formConfig?: FormExportConfig;

  formButtons: FormExportButton[] = [
    {
      actionId: 'submit',
      // con: 'pi pi-save',
      type: 'submit',
      label: 'common.info.forms.complianceForm.button.submit.label',
      translate: true,
      severity: 'primary',
    },
  ];*/

  initialized = false;

  constructor(
    private apiService: ApiService,
    private notificationsService: NotificationsService,
  ) {}

  ngOnChanges() {
    this.refreshFormItems();

    //this.refreshForm();
  }

  getShortDateFromDateStr(date: number): string {
    return getShortDateFromDateStr(date, undefined, true, true);
  }

  async refreshFormItems(): Promise<void> {
    this.allForms = await this.getCurrentComplianceForms();
    this.activeForm = find(this.allForms, { isActive: true });
    this.complianceFormItems = [];
    const newComplianceItems: ComplianceFormItem[] = [];
    for (
      let indexFilled = 0;
      indexFilled < (this.filledCompliance || []).length;
      indexFilled++
    ) {
      const thisForm = (this.filledCompliance as FilledCompliance[])[
        indexFilled
      ];
      const isCurrent =
        thisForm.isActive && thisForm.formUid === this.activeForm?.uid;
      const isApproved = this.isFormApproved(thisForm);
      const status = !isCurrent
        ? 'PREVIOUS'
        : isApproved
          ? 'ACTIVE'
          : 'PENDING';
      let severity = 'secondary';
      if (status === 'PENDING') {
        severity = 'warn';
      } else if (status === 'ACTIVE') {
        severity = 'success';
      }
      newComplianceItems.push({
        status: status,
        severity: severity,
        date: this.getShortDateFromDateStr(thisForm.createdAt),
        dateInt: thisForm.createdAt,
        canView: true,
        canApprove: isCurrent && this.isAdmin && !isApproved,
        isActive:
          thisForm.isActive && thisForm.formUid === this.activeForm?.uid,
        filledCompliance: thisForm,
      });
    }
    this.complianceFormItems = orderBy(newComplianceItems, 'dateInt', 'desc');
    this.initialized = true;
  }

  async fillNewForm(): Promise<void> {
    this.fillNewFormModel = {};
    this.fillNewFormConfig = JSON.parse(
      decompressAndDecodeData(
        (this.activeForm as TenantComplianceForm).formData,
      ),
    );
    this.showFillNewForm = true;
  }
  async viewForm(item: ComplianceFormItem): Promise<void> {
    this.selectedFormModel = !item.filledCompliance.modelData
      ? {}
      : JSON.parse(decompressAndDecodeData(item.filledCompliance.modelData));

    const formFound = find(this.allForms, {
      uid: item.filledCompliance.formUid,
    }) as TenantComplianceForm;

    if (!formFound) {
      console.error('Not found!');
      return;
    }
    this.selectedFormConfig = JSON.parse(
      decompressAndDecodeData(formFound.formData),
    );
    this.showSelectedForm = true;
  }
  closeSelectedDialog(): void {
    this.selectedFormConfig = undefined;
    this.selectedFormModel = undefined;
  }

  closeFillNewDialog(): void {
    this.fillNewFormConfig = undefined;
    this.fillNewFormModel = undefined;
  }

  async getCurrentComplianceForms(): Promise<TenantComplianceForm[]> {
    const getCurrentForms = new ApiRequestBuilder(
      'getEntityTypeAllComplianceForms',
    )
      .replaceUrl({
        entityType: this.entityType,
      })
      .build();

    const currentFormResponse = await this.apiService.callApi(getCurrentForms);
    if (currentFormResponse.status !== 'success') {
      return [];
    }
    return currentFormResponse.data as TenantComplianceForm[];
  }

  isFormApproved(form: FilledCompliance): boolean {
    if (!form) {
      return true;
    }
    if (form.manuallyApprovedAt) {
      return true;
    }

    const manualApprovalDataConfig = this.getManualApprovalDataConfig(form);
    if (manualApprovalDataConfig) {
      return !evaluateFormlyExpression(
        JSON.parse(decompressAndDecodeData(form.modelData)),
        manualApprovalDataConfig,
      );
    }
    return true;
  }

  getManualApprovalDataConfig(
    form: FilledCompliance,
  ): ExpressionContainer | undefined {
    const foundForm = find(this.allForms, { uid: form.formUid });
    if (!foundForm) {
      return undefined;
    }
    if (!foundForm?.manualApprovalData) {
      return undefined;
    }
    const manualApprovalDataConfig = JSON.parse(
      decompressAndDecodeData(foundForm.manualApprovalData as string),
    );
    return manualApprovalDataConfig;
  }
  /*async refreshForm(): Promise<void> {
    const getCurrentForm = new ApiRequestBuilder(
      'getEntityTypeCurrentComplianceForm',
    )
      .replaceUrl({
        entityType: this.entityType,
      })
      .build();

    const currentFormResponse = await this.apiService.callApi(getCurrentForm);
    if (currentFormResponse.status !== 'success') {
    }

    this.showMessageCompliance = '';
    this.showMessageSeverity = 'error';
    this.showComplianceForm = false;
    this.activeForm = currentFormResponse.data;
    console.log(this.activeForm);
    this.showComplianceForm = !!this.activeForm;
    if (!this.activeForm) {
      // Show message
      this.activeFilledForm = undefined;
      this.showMessageCompliance = 'noFormActive';
      this.initialized = true;
      return;
    }

    this.activeFilledForm = find(this.filledCompliance || [], {
      isActive: true,
      formUid: this.activeForm.uid,
    });
    console.log(this.filledCompliance);
    console.log(this.activeFilledForm);
    console.log(this.activeForm);
    console.log(
      decompressAndDecodeData(this.activeForm?.manualApprovalData as string),
    );

    if (!this.activeFilledForm) {
      this.previousActiveFilledForm = find(this.filledCompliance || [], {
        isActive: true,
      });
      if (!this.previousActiveFilledForm) {
        this.showMessageCompliance = 'noFilledForm';
      } else {
        this.showMessageCompliance = 'newFormVersionAvailable';
        this.showMessageSeverity = 'warn';
      }
    } else {
      this.previousActiveFilledForm = this.activeFilledForm;
    }

    this.formConfig = JSON.parse(
      decompressAndDecodeData(this.activeForm.formData),
    );
    this.formModel = !this.activeFilledForm
      ? {}
      : JSON.parse(decompressAndDecodeData(this.activeFilledForm.modelData));

    console.log(this.formModel);
    this.requiresManualApproval = false;
    const manualApprovalDataConfig = this.getManualApprovalDataConfig();
    if (manualApprovalDataConfig) {
      this.requiresManualApproval = evaluateFormlyExpression(
        this.formModel,
        manualApprovalDataConfig,
      );
    }

    this.initialized = true;
  }

  async onPayloadChangeComplianceForm(model: any): Promise<void> {
    this.requiresManualApproval = false;

    const manualApprovalDataConfig = this.getManualApprovalDataConfig();
    if (!manualApprovalDataConfig) {
      return;
    }
    console.log(model);

    this.requiresManualApproval = evaluateFormlyExpression(
      model,
      manualApprovalDataConfig,
    );
    console.log(this.activeForm);
  }*/

  async approveForm(item: ComplianceFormItem): Promise<void> {
    const updateComplianceFormRequest = new ApiRequestBuilder(
      'approveEntityComplianceForm',
    )
      .replaceUrl({
        environmentUid: this.environmentUid,
        entityId: this.entityUid,
        entityType: this.entityType,
      })
      .build();

    const updateComplianceFormResponse = await this.apiService.callApi(
      updateComplianceFormRequest,
    );

    if (
      updateComplianceFormResponse.status === 'success' &&
      updateComplianceFormResponse.data
    ) {
      this.notificationsService.showSuccessNotification(
        'common.info.forms.complianceForm.message.approvedSuccessFully',
      );

      this.entityUpdated.emit(updateComplianceFormResponse.data);
      await this.refreshFormItems();
    }
  }

  async onSubmitComplianceForm(model: any): Promise<void> {
    this.fillNewFormConfig = undefined;
    const updateComplianceFormRequest = new ApiRequestBuilder(
      'updateEntityComplianceForm',
    )
      .replaceUrl({
        environmentUid: this.environmentUid,
        entityId: this.entityUid,
        entityType: this.entityType,
      })
      .setBody(cloneDeep(model))
      .build();

    this.fillNewFormModel = undefined;
    const updateComplianceFormResponse = await this.apiService.callApi(
      updateComplianceFormRequest,
    );

    if (
      updateComplianceFormResponse.status === 'success' &&
      updateComplianceFormResponse.data
    ) {
      this.notificationsService.showSuccessNotification(
        'common.info.forms.complianceForm.message.updatedSuccessFully',
      );
      this.entityUpdated.emit(updateComplianceFormResponse.data);
      await this.refreshFormItems();
    }
  }
}
